import styles from './Privacy.module.css';

function Privacy() {
    return (
        <main className={styles.page}>
            <div className={`container ${styles.inner}`}>
                <h1 className={styles.title}>Privacy Policy</h1>
                <p className={styles.updated}>Last updated: April 15, 2026</p>

                <div className={styles.intro}>
                    <p>
                        At Aluvo, we take your privacy seriously. This Privacy Policy explains how we collect,
                        use, disclose, and safeguard your information when you use our mobile application and
                        website (collectively, the "Service").
                    </p>
                </div>

                <section className={styles.section}>
                    <h2>1. Information We Collect</h2>

                    <h3>Information You Provide</h3>
                    <p>
                        When you create an account and use Aluvo, we collect information you provide directly:
                    </p>
                    <ul>
                        <li><strong>Account Information:</strong> Username, email address, and password (encrypted)</li>
                        <li><strong>Profile Information:</strong> Optional profile photo, bio, and display preferences</li>
                        <li><strong>Watchlist Data:</strong> Movies and TV shows you add to your watchlist</li>
                        <li><strong>Quiz Responses:</strong> Your answers to verification quizzes and earned coins</li>
                        <li><strong>User Content:</strong> Reviews, ratings, and comments you post</li>
                    </ul>

                    <h3>Automatically Collected Information</h3>
                    <p>We automatically collect certain information when you use our Service:</p>
                    <ul>
                        <li><strong>Usage Data:</strong> Pages viewed, features used, time spent, and interactions</li>
                        <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers</li>
                        <li><strong>Log Data:</strong> IP address, browser type, access times, and referring URLs</li>
                        <li><strong>Analytics:</strong> Aggregate usage patterns and performance metrics</li>
                    </ul>

                    <h3>Third-Party Information</h3>
                    <p>
                        We integrate with third-party services to enhance your experience:
                    </p>
                    <ul>
                        <li><strong>The Movie Database (TMDB):</strong> Movie and TV show metadata, images, and ratings</li>
                        <li><strong>OAuth Providers:</strong> If you sign in with Google or Apple, we receive basic profile information</li>
                    </ul>
                </section>

                <section className={styles.section}>
                    <h2>2. How We Use Your Information</h2>
                    <p>We use the information we collect to:</p>
                    <ul>
                        <li><strong>Provide the Service:</strong> Create your account, manage your watchlist, deliver quizzes, and award coins</li>
                        <li><strong>Personalize Experience:</strong> Recommend content based on your preferences and viewing history</li>
                        <li><strong>Communicate:</strong> Send you notifications, updates, and promotional emails (with your consent)</li>
                        <li><strong>Improve Our Service:</strong> Analyze usage patterns, fix bugs, and develop new features</li>
                        <li><strong>Security:</strong> Detect fraud, prevent abuse, and protect user accounts</li>
                        <li><strong>Legal Compliance:</strong> Comply with applicable laws and respond to legal requests</li>
                    </ul>
                </section>

                <section className={styles.section}>
                    <h2>3. Sharing Your Information</h2>

                    <h3>Public Information</h3>
                    <p>
                        By default, certain information is visible to other Aluvo users:
                    </p>
                    <ul>
                        <li>Your username and profile photo</li>
                        <li>Your coin collection and earned achievements</li>
                        <li>Public watchlists (you can mark watchlists as private)</li>
                        <li>Reviews and ratings you post</li>
                    </ul>
                    <p>You can adjust these privacy settings in your account preferences.</p>

                    <h3>We Do Not Sell Your Data</h3>
                    <p>
                        We do not sell, rent, or trade your personal information to third parties for marketing purposes.
                    </p>

                    <h3>Service Providers</h3>
                    <p>We may share your information with trusted service providers who help us operate:</p>
                    <ul>
                        <li><strong>Cloud Hosting:</strong> For data storage and application hosting</li>
                        <li><strong>Analytics Services:</strong> To understand usage patterns (anonymized data)</li>
                        <li><strong>Email Services:</strong> To send notifications and updates</li>
                        <li><strong>Authentication:</strong> OAuth providers for secure sign-in</li>
                    </ul>
                    <p>These providers are contractually obligated to protect your data and use it only for specified purposes.</p>

                    <h3>Legal Requirements</h3>
                    <p>We may disclose your information if required by law, court order, or government request, or to protect the rights, property, or safety of Aluvo, our users, or others.</p>
                </section>

                <section className={styles.section}>
                    <h2>4. Data Retention</h2>
                    <p>
                        We retain your personal information for as long as your account is active or as needed to provide you services.
                        If you delete your account, we will delete or anonymize your personal information within 30 days, except where
                        we need to retain it for legal compliance, dispute resolution, or security purposes.
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>5. Your Privacy Rights</h2>
                    <p>Depending on your location, you may have the following rights:</p>
                    <ul>
                        <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                        <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                        <li><strong>Deletion:</strong> Request deletion of your account and personal data</li>
                        <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                        <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                        <li><strong>Object:</strong> Object to certain processing of your information</li>
                    </ul>
                    <p>To exercise these rights, contact us at privacy@aluvo.app</p>
                </section>

                <section className={styles.section}>
                    <h2>6. Security</h2>
                    <p>
                        We implement industry-standard security measures to protect your information:
                    </p>
                    <ul>
                        <li>Encryption in transit (HTTPS/TLS) and at rest</li>
                        <li>Secure password hashing with bcrypt</li>
                        <li>Regular security audits and vulnerability assessments</li>
                        <li>Access controls and authentication mechanisms</li>
                    </ul>
                    <p>
                        However, no method of transmission over the internet is 100% secure. While we strive to protect
                        your information, we cannot guarantee absolute security.
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>7. Children's Privacy</h2>
                    <p>
                        Aluvo is not intended for children under 13 years of age. We do not knowingly collect personal
                        information from children under 13. If we learn we have collected information from a child under 13,
                        we will delete it immediately. If you believe a child has provided us with personal information,
                        please contact us at privacy@aluvo.app
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>8. International Users</h2>
                    <p>
                        Your information may be transferred to and processed in countries other than your own. These countries
                        may have different data protection laws. By using Aluvo, you consent to the transfer of your information
                        to our facilities and service providers worldwide.
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>9. Cookies and Tracking</h2>
                    <p>
                        We use cookies and similar tracking technologies to:
                    </p>
                    <ul>
                        <li>Remember your login session</li>
                        <li>Store your preferences</li>
                        <li>Analyze usage patterns</li>
                        <li>Improve performance and user experience</li>
                    </ul>
                    <p>You can control cookies through your browser settings, but disabling them may affect functionality.</p>
                </section>

                <section className={styles.section}>
                    <h2>10. Changes to This Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time. We will notify you of significant changes by
                        posting the new policy on this page and updating the "Last updated" date. We encourage you to review
                        this policy periodically.
                    </p>
                </section>

                <section className={styles.section}>
                    <h2>11. Contact Us</h2>
                    <p>
                        If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
                    </p>
                    <ul className={styles.contactList}>
                        <li><strong>Email:</strong> privacy@aluvo.app</li>
                        <li><strong>Support:</strong> support@aluvo.app</li>
                    </ul>
                </section>

                <div className={styles.footer}>
                    <p>
                        By using Aluvo, you acknowledge that you have read and understood this Privacy Policy
                        and agree to its terms.
                    </p>
                </div>
            </div>
        </main>
    );
}

export default Privacy;